import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, X, MapPin, User, Wrench, Loader2, CheckCircle2, RotateCcw, Ban } from "lucide-react";
import { api, formatApiError, floorLabel } from "@/lib/api";
import { useUnit } from "@/context/UnitContext";
import { UnitToggle } from "@/components/UnitToggle";
import { mToUnit, unitToM, unitLabel, convertDisplay } from "@/lib/units";
import RetailerLayout from "@/components/RetailerLayout";

const STATUSES = ["offered", "accepted", "completed", "cancelled"];
const STATUS_STYLE = {
  offered: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  completed: "bg-[#0F3D2E]/10 text-[#0F3D2E]",
  cancelled: "bg-slate-100 text-slate-500",
};
const JOB_TYPES = [
  { value: "collection_fitting", label: "Collection & fitting" },
  { value: "fitting", label: "Fitting only" },
  { value: "collection", label: "Collection" },
  { value: "delivery", label: "Delivery" },
  { value: "measuring", label: "Measuring service" },
];
const FLOORING = ["carpet", "vinyl", "laminate", "lvt", "carpet_tiles", "underlay", "other"];

const EMPTY_FORM = {
  postcode: "",
  job_type: "collection_fitting",
  flooring_type: "",
  customer_id: "",
  note: "",
  room_name: "Room",
  width_m: "",
  length_m: "",
};

export default function AdminOpportunities() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    try {
      const r = await api.get("/admin/opportunities", { params });
      setRows(r.data.opportunities || []);
      setOpen(r.data.open_commitments || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function setStatusFor(id, next) {
    setBusyId(id);
    try {
      await api.patch(`/admin/opportunities/${id}`, { status: next });
      toast.success(`Marked ${next}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <RetailerLayout>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-opportunities-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Fitter opportunities</h1>
            <p className="text-sm text-slate-500 mt-0.5">Jobs offered to approved local fitters. {open} currently accepted.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold text-sm shrink-0"
            data-testid="admin-create-opportunity"
          >
            <Plus className="w-4 h-4" /> New opportunity
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 flex flex-wrap items-center gap-2" data-testid="opp-filters">
          <button
            onClick={() => setStatus("")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${status === "" ? "bg-[#E05915] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="opp-filter-all"
          >
            All
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold capitalize ${status === s ? "bg-[#E05915] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`opp-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="opps-empty">No opportunities{status ? ` with status "${status}"` : ""} yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="opps-list">
              {rows.map((o) => (
                <li key={o.id} className="p-4" data-testid={`opp-row-${o.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{o.title || "Flooring job"}</span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[o.status] || "bg-slate-100 text-slate-500"}`} data-testid={`opp-status-${o.id}`}>
                          {o.status}
                        </span>
                        <span className="text-[11px] font-mono text-slate-400">{o.reference_code}</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="capitalize">{(o.job_type || "").replace(/_/g, " ")}</span>
                        {o.flooring_type ? <span>· {floorLabel(o.flooring_type)}</span> : null}
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {o.postcode}{o.district ? ` · ${o.district}` : ""}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {o.customer_name || o.customer_email || `via ${o.source || "unknown"}`}</span>
                        {o.accepted_fitter_name ? <span className="inline-flex items-center gap-1 text-emerald-700"><Wrench className="w-3 h-3" /> {o.accepted_fitter_name}</span> : null}
                        {o.declined_count ? <span>· {o.declined_count} declined</span> : null}
                        {o.rooms?.length ? <span>· {o.rooms.length} {o.rooms.length === 1 ? "room" : "rooms"}</span> : null}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {o.status === "accepted" && (
                        <button onClick={() => setStatusFor(o.id, "completed")} disabled={busyId === o.id} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F3D2E] hover:underline disabled:opacity-50" data-testid={`opp-complete-${o.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                        </button>
                      )}
                      {["offered", "accepted"].includes(o.status) && (
                        <button onClick={() => setStatusFor(o.id, "cancelled")} disabled={busyId === o.id} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-600 disabled:opacity-50" data-testid={`opp-cancel-${o.id}`}>
                          <Ban className="w-3.5 h-3.5" /> Cancel
                        </button>
                      )}
                      {o.status === "cancelled" && (
                        <button onClick={() => setStatusFor(o.id, "offered")} disabled={busyId === o.id} className="inline-flex items-center gap-1 text-xs font-semibold text-[#E05915] hover:underline disabled:opacity-50" data-testid={`opp-reopen-${o.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" /> Reopen
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {showCreate && <CreateDrawer onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </RetailerLayout>
  );
}

function CreateDrawer({ onClose, onCreated }) {
  const { unit } = useUnit();
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.postcode.trim()) { toast.error("Postcode is required"); return; }
    const rooms = [];
    const widthM = unitToM(form.width_m, unit);
    const lengthM = unitToM(form.length_m, unit);
    if (widthM && lengthM) {
      rooms.push({
        name: form.room_name || "Room",
        width_m: widthM,
        length_m: lengthM,
        flooring_type: form.flooring_type || null,
      });
    }
    setBusy(true);
    try {
      await api.post("/admin/opportunities", {
        postcode: form.postcode.trim(),
        job_type: form.job_type,
        flooring_type: form.flooring_type || null,
        customer_id: form.customer_id.trim() || null,
        note: form.note.trim() || null,
        rooms,
        source: "admin",
      });
      toast.success("Opportunity created and offered to nearby fitters");
      onCreated();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="create-opp-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl text-slate-900">New opportunity</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="create-opp-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Postcode" required>
            <input className="fm-input" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} placeholder="e.g. M4 1HW" data-testid="opp-postcode" />
          </Field>
          <Field label="Job type">
            <select className="fm-input" value={form.job_type} onChange={(e) => set("job_type", e.target.value)} data-testid="opp-job-type">
              {JOB_TYPES.map((j) => <option key={j.value} value={j.value}>{j.label}</option>)}
            </select>
          </Field>
          <Field label="Flooring type (optional)">
            <select className="fm-input" value={form.flooring_type} onChange={(e) => set("flooring_type", e.target.value)} data-testid="opp-flooring-type">
              <option value="">Not specified</option>
              {FLOORING.map((f) => <option key={f} value={f}>{floorLabel(f)}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Room name"><input className="fm-input" value={form.room_name} onChange={(e) => set("room_name", e.target.value)} data-testid="opp-room-name" /></Field>
            <Field label="Width (m)"><input type="number" step="0.01" className="fm-input" value={form.width_m} onChange={(e) => set("width_m", e.target.value)} data-testid="opp-room-width" /></Field>
            <Field label="Length (m)"><input type="number" step="0.01" className="fm-input" value={form.length_m} onChange={(e) => set("length_m", e.target.value)} data-testid="opp-room-length" /></Field>
          </div>
          <Field label="Customer ID (optional)">
            <input className="fm-input" value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} placeholder="Link to a known customer" data-testid="opp-customer-id" />
          </Field>
          <Field label="Note to fitters (optional)">
            <textarea className="fm-input min-h-[80px]" value={form.note} onChange={(e) => set("note", e.target.value)} maxLength={500} data-testid="opp-note" />
          </Field>
          <button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="opp-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create &amp; offer to fitters
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-500 mb-1.5 block">{label}{required ? " *" : ""}</span>
      {children}
    </label>
  );
}
